import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { useAuth } from '../../hooks/useAuth';
import { SOP } from '../../types/sop';
import { toast } from '../ui/use-toast';
import { sopFlagService } from '@/services/sopFlagService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAppSettings } from '@/context/AppSettingsContext';

interface FlagForReviewDialogProps {
  sop: SOP;
  onFlagged: () => void;
}

/**
 * Dialog component for flagging SOPs for review
 */
export const FlagForReviewDialog: React.FC<FlagForReviewDialogProps> = ({ sop, onFlagged }) => {
  const { user, displayName } = useAuth();
  const { appSettings } = useAppSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // If the feature is disabled, don't render the component
  if (!appSettings.showSOPFlaggingFeature) {
    return null;
  }

  const handleFlagForReview = async () => {
    if (!user?.uid || !reason.trim()) return;

    try {
      setIsLoading(true);
      await sopFlagService.flagSOP(
        sop.id, 
        reason, 
        displayName || user.email || 'Unknown User'
      );
      
      setIsOpen(false);
      setReason('');
      onFlagged();
      
      toast({
        title: "Success",
        description: "SOP has been flagged for review",
      });
    } catch (error) {
      console.error('Error flagging SOP:', error);
      toast({
        title: "Error",
        description: "Failed to flag SOP for review",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400">
                <Flag className="h-4 w-4" />
                <span className="hidden md:inline">Flag For Review</span>
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Think this SOP doesn't belong here? Flag it for review!</p>
          </TooltipContent>
        </Tooltip>
        
        <DialogContent className="bg-white dark:bg-gray-900" aria-describedby="flag-description">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">Flag SOP For Review</DialogTitle>
            <DialogDescription id="flag-description" className="text-gray-600 dark:text-gray-400">
              Please provide a reason why this SOP should be reviewed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Textarea
              placeholder="Enter reason for review..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-h-[100px]"
            />
            <div className="flex gap-2">
              <Button 
                onClick={handleFlagForReview} 
                disabled={!reason.trim() || isLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white"
              >
                {isLoading ? 'Submitting...' : 'Submit Flag'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsOpen(false);
                  setReason('');
                }}
                className="flex-1 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default FlagForReviewDialog;